"use client"

import { useId, useMemo, useRef, useState } from "react"
import { PROMPT_SNIPPET_CHAT_TYPES, type PromptSnippetChatType, type PromptSnippetInput } from "@/schema/prompt-snippet"
import { SelectInput } from "@ui/forms/inputs/select-input"
import { Button, Input, Label } from "@ui/shad"
import { RotateCcw } from "lucide-react"

import { IconColorPickerInput } from "@/components/automations/edit-panel/triggers/icon-color-picker-input"
import { SimpleFormDialog } from "@/components/dialogs"
import { Editor, type EditorRefHandle } from "@/components/lexical"

const CHAT_TYPE_OPTIONS = PROMPT_SNIPPET_CHAT_TYPES.map((chatType) => ({
  value: chatType,
  label: chatType,
}))

interface PromptSnippetDialogFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  dialogTitle: string
  defaultValues: PromptSnippetInput
  onSubmit: (values: PromptSnippetInput) => Promise<void>
  onCancel: () => void
  onRollback?: () => void
  submitting?: boolean
  submitLabel?: string
  initialBookOptions?: { id: string; name: string }[]
  bookOptionsError?: boolean
  bookOptionsLoading?: boolean
  onRetryBookOptions?: () => void
  preferredBookId?: string
  managePromptSnippetsHref?: string
}

export function PromptSnippetDialogForm({
  open,
  onOpenChange,
  dialogTitle,
  defaultValues,
  onSubmit,
  onCancel,
  onRollback,
  submitting = false,
  submitLabel = "Save",
  managePromptSnippetsHref,
  initialBookOptions,
  bookOptionsError = false,
  bookOptionsLoading = false,
  onRetryBookOptions,
  preferredBookId,
}: PromptSnippetDialogFormProps) {
  const formId = useId()
  const editorRef = useRef<EditorRefHandle | null>(null)
  const [title, setTitle] = useState(defaultValues.title)
  const [content, setContent] = useState(defaultValues.content)
  const [icon, setIcon] = useState(defaultValues.icon)
  const [color, setColor] = useState(defaultValues.color)
  const [chatType, setChatType] = useState<PromptSnippetChatType>(defaultValues.chat_type)

  const [selectedBookId, setSelectedBookId] = useState<string | null>(null)
  const initialBookId =
    selectedBookId ??
    initialBookOptions?.find((book) => book.id === preferredBookId)?.id ??
    initialBookOptions?.[0]?.id ??
    ""

  const canSubmit = useMemo(
    () => title.trim() && content.trim() && chatType && (!initialBookOptions || initialBookId),
    [title, content, chatType, initialBookOptions, initialBookId]
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    await onSubmit({
      title: title.trim(),
      content: content.trim(),
      icon,
      color,
      chat_type: chatType,
      ...(initialBookOptions ? { book_ids: [initialBookId] } : {}),
    })
  }

  return (
    <SimpleFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={dialogTitle}
      action={{ label: submitLabel, form: formId, disabled: !canSubmit, pending: submitting }}
      cancel={{ onClick: onCancel, disabled: submitting }}
    >
      <form id={formId} onSubmit={handleSubmit} className="space-y-2">
        {initialBookOptions && initialBookOptions.length > 0 ? (
          <SelectInput<string>
            id={`${formId}-book`}
            label="Book"
            value={initialBookId}
            onChange={(value: string | null | undefined) => value && setSelectedBookId(value)}
            options={initialBookOptions.map((book) => ({ value: book.id, label: book.name }))}
          />
        ) : initialBookOptions ? (
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            {bookOptionsError
              ? "Couldn't load books."
              : bookOptionsLoading
                ? "Loading books..."
                : "No books available yet."}
            {bookOptionsError && onRetryBookOptions && (
              <Button type="button" variant="link" size="sm" onClick={onRetryBookOptions}>
                Retry
              </Button>
            )}
          </div>
        ) : null}
        <div className="space-y-2">
          <div className="space-y-1.5">
            <Label htmlFor="snippet-title">Title</Label>
            <Input
              id="snippet-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Meeting recap"
              autoFocus
              required
            />
          </div>

          <IconColorPickerInput
            id="snippet-icon-color"
            label="Icon and color"
            iconValue={icon}
            colorValue={color}
            onIconChange={setIcon}
            onColorChange={setColor}
          />

          <SelectInput<string>
            id="snippet-chat-type"
            label="Chat type"
            value={chatType}
            onChange={(value: string | null | undefined) => {
              if (value) setChatType(value as PromptSnippetChatType)
            }}
            options={CHAT_TYPE_OPTIONS}
            placeholder="Select a chat type"
          />

          <div className="space-y-1.5">
            <Label>Content</Label>
            <div className="border-border rounded-md border p-2">
              <Editor
                ref={editorRef}
                isPlainText={true}
                initialContent={{ markdown: content, preferred_format: "markdown" }}
                onChange={(value) => setContent(value.markdown || value.html || "")}
                startingPlaceholder="Write the prompt snippet. Longer instructions work well here."
                styleOverrides={{
                  "--lexical-editor-font-size": "14px",
                  "--lexical-editor-min-height": "180px",
                  "--lexical-editor-max-height": "260px",
                }}
              />
            </div>
          </div>
        </div>

        {onRollback ? (
          <Button type="button" variant="ghost" size="sm" onClick={onRollback} disabled={submitting}>
            <RotateCcw />
            Reset to default
          </Button>
        ) : managePromptSnippetsHref ? (
          <a href={managePromptSnippetsHref} className="text-muted-foreground text-xs hover:underline">
            Manage all prompt snippets
          </a>
        ) : (
          <span className="text-xs" />
        )}
      </form>
    </SimpleFormDialog>
  )
}
